import { useToolOwnership } from "../shared/toolOwnership"
/**
 * WiseFriend Integration (Beta) renderer.
 *
 * Renders the meeting with a Daily call object and Daily React instead of the Prebuilt
 * iframe. Everything Daily-specific for the custom experience lives under `call/custom/`;
 * shared meeting lifecycle stays in the shell and reaches this surface through the
 * renderer contract in `../types`.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import type { DailyCall, DailyFactoryOptions } from "@daily-co/daily-js"
import {
  DailyAudio,
  DailyProvider,
  useCallObject,
  useDaily,
  useDailyEvent,
  useDevices,
  useLocalSessionId,
  useNetwork,
  useParticipantCounts,
  useParticipantProperty,
  usePermissions,
  useScreenShare,
} from "@daily-co/daily-react"
import { Loader2, WifiOff } from "lucide-react"
import { requiresMeetingAdmission, type CallSurfaceHandle, type CallSurfaceProps, type MessageOpenTarget } from "../types"
import { useMainRoomTarget } from "./useMainRoomTarget"
import MeetingMessagesPanel, { type MessageDraftState, type MessageDrafts } from "../../MeetingMessagesPanel"
import { getPanelTokenFromUrl } from "../shared/panelUrls"
import { BreakoutPanel } from "./BreakoutPanel"
import { CallSettingsDialog, type CallSettingsPane } from "./CallSettingsDialog"
import { ChatPanel } from "./ChatPanel"
import { ChatNotification, useChatNotification } from "./ChatNotification"
import { MessageNotification } from "../shared/MessageNotification"
import { CONTROLS_BAR_RESERVED_SPACE, ControlsBar, canShareScreenInThisBrowser } from "./ControlsBar"
import { describeDeviceState } from "./DeviceSettingsMenu"
import { isNativeNoiseReductionPending } from "./nativeNoiseReduction"
import { cancelVideoEffectOperations, retryCameraWithVideoEffect, useVideoEffects } from "./videoEffects"
import { MicLevelMeter } from "./MicLevelMeter"
import { MoreMenu } from "./MoreMenu"
import { QuickDeviceMenu } from "./QuickDeviceMenu"
import { NetworkPanel } from "./NetworkPanel"
import { ReactionsMenu, ReactionsOverlay } from "./Reactions"
import { InspireInline, type InspireStagePlacement } from "./InspireInline"
import { InspireStage } from "./InspireStage"
import { MeetingPollStage, MeetingPollTools } from "./MeetingPolls"
import { useMeetingPolls } from "./useMeetingPolls"
import { useMeetingPollAdmission } from "./useMeetingPollAdmission"
import { PeoplePanel } from "./PeoplePanel"
import { PrejoinScreen, type PrejoinChoice } from "./PrejoinScreen"
import { RecordingTranscriptionBadges } from "./RecordingTranscriptionBadges"
import { Stage, type StageViewMode } from "./Stage"
import { WaitingScreen } from "./WaitingScreen"
import { WorkspaceHeader } from "./WorkspaceHeader"
import { MeetingDetailsPanel, PanelIframe, WorkspacePanels, type WorkspaceOverlay, type WorkspaceTool, type WorkspaceToolId } from "./WorkspacePanels"
import { usePanelPresence } from "./usePanelPresence"
import { useDirectMessageSignals } from "./useDirectMessageSignals"
import { DEVICE_NOTICE_DISMISS_MS, HOST_MEDIA_CHANGE_GRACE_MS } from "./constants"
import { useCallLayout } from "./useCallLayout"
import { destroyStaleCallInstance, useDailyCallObjectLifecycle } from "./useDailyCallObjectLifecycle"
import { useBreakoutCountdown } from "./useBreakoutCountdown"
import { useBreakoutRooms } from "./useBreakoutRooms"
import { useBreakoutTranscription } from "./useBreakoutTranscription"
import { useCallChat } from "./useCallChat"
import { CallPreferencesProvider, useCallPreferences } from "./useCallPreferences"
import { useInspireSync } from "./useInspireSync"
import { useVideoQuality } from "./useVideoQuality"
import { describePictureInPictureFailure, usePictureInPicture } from "./usePictureInPicture"
import { RaisedHandsProvider, useRaisedHands } from "./useRaisedHands"
import { useReactions } from "./useReactions"
import { PictureInPictureWindow } from "./PictureInPictureWindow"
import { createPortal } from "react-dom"
import { ForNextTimePanel, useMentorshipRelationships, type ForNextTimePanelHandle } from "components/mentorship/continuity"

/** Stable factory options: daily-react recreates the instance when this object changes. */
const CALL_OBJECT_OPTIONS: DailyFactoryOptions = {
  subscribeToTracksAutomatically: true,
}

type CustomCallControllerProps = CallSurfaceProps & { callObject: DailyCall }

/** Daily's camera-error payload carries an error type; map it to the shared device guidance. */
export const describeCameraError = (event: any): string => {
  const type = event?.error?.type || event?.errorMsg?.type
  switch (type) {
    case "permissions":
      return "Camera or microphone access is blocked. Allow it in your browser's site settings, then try again."
    case "not-found":
      return "The selected camera or microphone was not found. Pick a different device in the settings."
    case "in-use":
      return "Your camera or microphone is in use by another app. Close it, then try again."
    default:
      return event?.errorMsg?.errorMsg || event?.error?.msg || "We could not start your camera or microphone. Try again or pick a different device."
  }
}

const CustomCallController = forwardRef<CallSurfaceHandle, CustomCallControllerProps>(function CustomCallController(props, ref) {
  const {
    callObject,
    meetingId,
    joinInfo,
    onJoinInfoRefresh,
    meeting,
    activeMeeting,
    attendee,
    waitRoom,
    questions,
    messages,
    onJoined,
    onWaiting,
    onAccessGranted,
    onAccessDenied,
    onParticipantCount,
    onMessagesSurfaceOpenChange,
    onDirectMessageSignal,
    onError,
    onLeft,
    subscribeMeetingEvents,
  } = props
  const containerRef = useRef<HTMLDivElement | null>(null)
  const stageVideoRef = useRef<HTMLVideoElement>(null)
  const layout = useCallLayout(containerRef)
  const daily = useDaily()
  const videoEffects = useVideoEffects()
  const localSessionId = useLocalSessionId()
  const micOn = Boolean(useParticipantProperty(localSessionId, "audio"))
  const camOn = Boolean(useParticipantProperty(localSessionId, "video"))
  const { camState, micState } = useDevices()
  const [deviceNotice, setDeviceNotice] = useState<string | null>(null)
  const deviceNoticeTimerRef = useRef<number | null>(null)
  const showDeviceNotice = useCallback((message: string | null) => {
    if (deviceNoticeTimerRef.current) window.clearTimeout(deviceNoticeTimerRef.current)
    deviceNoticeTimerRef.current = null
    setDeviceNotice(message)
    if (message) {
      deviceNoticeTimerRef.current = window.setTimeout(() => setDeviceNotice(null), DEVICE_NOTICE_DISMISS_MS)
    }
  }, [])
  useEffect(() => () => {
    if (deviceNoticeTimerRef.current) window.clearTimeout(deviceNoticeTimerRef.current)
  }, [])
  const { canSendAudio, canSendVideo, canAdminParticipants } = usePermissions()
  const { isSharingScreen, startScreenShare, stopScreenShare } = useScreenShare()
  const callPreferences = useCallPreferences()
  const { preferences } = callPreferences
  // Google Meet's automatic picture-in-picture setting: which browser moments may open the floating call.
  const autoPipMode = preferences.autoPictureInPicture
  const pipAutoEnter = autoPipMode === "always" || autoPipMode === "tab-switch" || (autoPipMode === "screen-share" && isSharingScreen)
  const { present: participantCount } = useParticipantCounts()

  const resolveMainRoom = useMainRoomTarget({ meetingId, attendee, joinInfo, onJoinInfoRefresh })
  const lifecycle = useDailyCallObjectLifecycle({
    callObject,
    joinInfo,
    displayName: attendee.displayName,
    onJoined,
    onWaiting,
    onAccessGranted,
    onAccessDenied,
    onError,
    onLeft,
    resolveMainRoom,
  })
  const { stage, join, leave, switchRoom, switchingTo, setRejoinTarget } = lifecycle
  const inCall = stage === "in-call"

  const [viewMode, setViewMode] = useState<StageViewMode>("speaker")
  const [pinnedId, setPinnedId] = useState<string | null>(null)
  const togglePin = useCallback((sessionId: string) => {
    const next = pinnedId === sessionId ? null : sessionId
    setPinnedId(next)
    if (next) setViewMode("speaker")
  }, [pinnedId])
  useDailyEvent("participant-left", useCallback((event: any) => {
    const id = event?.participant?.session_id
    if (id) setPinnedId((current) => (current === id ? null : current))
  }, []))
  // The tools panel starts collapsed at every size (the owner wanted the video alone on join); Tools opens it.
  const [panelOpen, setPanelOpen] = useState(false)
  const togglePanel = useCallback(() => setPanelOpen((value) => !value), [])
  const [activeTab, setActiveTab] = useState<WorkspaceToolId>("meeting")
  const continuityPanelRef = useRef<ForNextTimePanelHandle>(null)
  const [devicesOpen, setDevicesOpen] = useState(false)
  const [settingsPane, setSettingsPane] = useState<CallSettingsPane>("audio")
  const [reactionsOpen, setReactionsOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [micMenuOpen, setMicMenuOpen] = useState(false)
  const [camMenuOpen, setCamMenuOpen] = useState(false)
  const closePopovers = useCallback(() => {
    setReactionsOpen(false)
    setMoreOpen(false)
    setMicMenuOpen(false)
    setCamMenuOpen(false)
  }, [])
  const openSettings = useCallback((pane: CallSettingsPane) => {
    setSettingsPane(pane)
    setDevicesOpen(true)
  }, [])
  const [networkOpen, setNetworkOpen] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  /** The thread or person the direct-messages panel should open; the key makes repeat requests distinct. */
  const [messagesTarget, setMessagesTarget] = useState<{ key: number; threadId?: string; recipientIdentityKey?: string } | null>(null)
  const messagesTargetKeyRef = useRef(0)
  const messagesOpenRef = useRef(false)
  const panelDrawer = layout.panelMode === "drawer"
  const panelDrawerRef = useRef(panelDrawer)
  panelDrawerRef.current = panelDrawer
  // Responsive geometry changes presentation, not the user's open-tool intent.
  // Keep this same panel tree mounted across keyboard dismissal and rotation,
  // including calls without continuity retention. Explicit Close still closes it.


  const inspirePanel = joinInfo.inspire_panel?.enabled ? joinInfo.inspire_panel : undefined
  const inspirePresenter = inspirePanel?.role === "presenter"
  // A live card takes the stage for everyone (as the legacy Prebuilt "main" integration did);
  // each attendee can hide it locally, and the next card or shared response brings it back.
  const [inspireOnStage, setInspireOnStage] = useState(true)
  const handleInspireVisibility = useCallback((visible: boolean) => {
    if (visible) {
      setInspireOnStage(true)
      if (inspirePresenter) {
        // Presenters keep their controls in the Inspire tab beside the card.
        setActiveTab("inspire")
        setPanelOpen(true)
      }
      return
    }
    if (panelDrawerRef.current) {
      setPanelOpen(false)
    }
  }, [inspirePresenter])
  const { inspireActive, inspireContentLive, sendControlAction, syncVersion: inspireSyncVersion, refresh: refreshInspire } = useInspireSync({
    meetingId,
    panel: inspirePanel,
    enabled: inCall,
    onActivityVisibilityChange: handleInspireVisibility,
  })
  const pollPanel = useMeetingPollAdmission({ meetingId, panel: joinInfo.poll_panel?.enabled ? joinInfo.poll_panel : undefined, enabled: inCall })
  const handlePollDisplay = useCallback(() => {
    if (pollPanel?.role === "presenter" && !panelDrawerRef.current) {
      setActiveTab("polls")
      setPanelOpen(true)
    } else if (panelDrawerRef.current) {
      setPanelOpen(false)
    }
  }, [pollPanel?.role])
  const polls = useMeetingPolls({ meetingId, panel: pollPanel, enabled: inCall, reconnecting: lifecycle.reconnecting, onDisplay: handlePollDisplay })
  const refreshPolls = polls.refresh
  // Daily app messages reach peers, not their sender. Refresh local poll admission after
  // ending an Inspire round instead of leaving its launch guard stale until the next poll.
  useEffect(() => { if (inCall) void refreshPolls() }, [inCall, inspireContentLive, inspireSyncVersion, refreshPolls])
  const pollLive = Boolean(polls.data?.active_poll || polls.data?.displayed_poll)
  // Launching a poll can put a live Inspire Card away on the server. Peers hear about it, but Daily
  // does not echo app messages to their sender, so this page re-reads the card itself.
  const activePollId = polls.data?.active_poll?.id
  useEffect(() => { if (inCall && activePollId) refreshInspire() }, [activePollId, inCall, refreshInspire])

  const pip = usePictureInPicture({
    videoRef: stageVideoRef,
    enabled: inCall,
    autoEnter: pipAutoEnter,
    isSharingScreen,
    autoEnterOnShare: autoPipMode === "always" || autoPipMode === "screen-share",
  })

  // Prebuilt parity: raised hands, reactions, room chat, and network quality.
  const handleRemoteHandRaised = useCallback((name: string) => showDeviceNotice(`${name} raised their hand`), [showDeviceNotice])
  const hands = useRaisedHands({ enabled: inCall, onRemoteHandRaised: handleRemoteHandRaised })
  const { reactions, sendReaction, announcement: reactionAnnouncement } = useReactions({ enabled: inCall })
  const onDirectMessageSignalRef = useRef(onDirectMessageSignal)
  onDirectMessageSignalRef.current = onDirectMessageSignal
  const { sendSignal: sendDirectMessageSignal } = useDirectMessageSignals({
    enabled: inCall,
    onSignal: (signal) => onDirectMessageSignalRef.current?.({ type: "direct_message.created", thread_id: signal.threadId, sender_identity_key: signal.senderIdentityKey, recipient_identity_key: signal.recipientIdentityKey }),
  })
  const chatVisible = inCall && panelOpen && activeTab === "chat"
  const chatNotice = useChatNotification({ enabled: inCall, isOpen: chatVisible, mode: preferences.chatNotifications })
  const chat = useCallChat({ enabled: inCall, isOpen: chatVisible, onIncoming: chatNotice.notify })
  useVideoQuality({ preset: preferences.videoQuality, enabled: inCall })
  const breakout = useBreakoutRooms({
    admissionToken: pollPanel?.panel_token,
    meetingId,
    attendee,
    joinInfo,
    enabled: inCall,
    isHost: Boolean(canAdminParticipants),
    subscribeMeetingEvents,
    switchRoom,
    setRejoinTarget,
    mainRoomRecoveryVersion: lifecycle.mainRoomRecoveryVersion,
    onNotice: showDeviceNotice,
  })
  const breakoutCountdown = useBreakoutCountdown(breakout.endsAt)
  useBreakoutTranscription({ roomId: breakout.location.kind === "room" ? breakout.location.roomId : undefined, inRoom: breakout.location.kind === "room", enabled: inCall, startInMain: Boolean(joinInfo.can_start_transcription) })
  const { threshold: networkThreshold } = useNetwork()
  const previousThresholdRef = useRef(networkThreshold)
  useEffect(() => {
    if (inCall && networkThreshold === "very-low" && previousThresholdRef.current !== "very-low") {
      showDeviceNotice("Your network connection is weak. Video quality may drop until it recovers.")
    }
    previousThresholdRef.current = networkThreshold
  }, [inCall, networkThreshold, showDeviceNotice])

  useEffect(() => {
    onParticipantCount(participantCount)
  }, [onParticipantCount, participantCount])

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement))
    document.addEventListener("fullscreenchange", handleFullscreenChange)
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange)
  }, [])

  const messagesVisible = inCall && panelOpen && activeTab === "messages"
  useEffect(() => {
    if (messagesOpenRef.current === messagesVisible) return
    messagesOpenRef.current = messagesVisible
    onMessagesSurfaceOpenChange(messagesVisible)
  }, [messagesVisible, onMessagesSurfaceOpenChange])

  const openMessages = useCallback((target?: MessageOpenTarget) => {
    if (!joinInfo.messages_panel?.enabled) return
    // A new key each time: with a thread or recipient the panel opens it, without one it returns to the list.
    messagesTargetKeyRef.current += 1
    setMessagesTarget({ key: messagesTargetKeyRef.current, threadId: target?.threadId, recipientIdentityKey: target?.recipientIdentityKey })
    setActiveTab("messages")
    setPanelOpen(true)
  }, [joinInfo.messages_panel])
  /** Leaves an overlay panel (chat, direct messages): back to the tabs, and on phones close the drawer. */
  const closeOverlay = useCallback((backTo: WorkspaceToolId = "meeting") => {
    setActiveTab(backTo)
    // On phones, Back from chat closes the drawer to reveal the video again; Back to People keeps it open on People.
    if (panelDrawerRef.current && backTo === "meeting") setPanelOpen(false)
  }, [])
  const chatOpen = panelOpen && activeTab === "chat"
  const toggleChat = useCallback(() => {
    if (chatOpen) {
      closeOverlay()
      return
    }
    setActiveTab("chat")
    setPanelOpen(true)
  }, [chatOpen, closeOverlay])

  const isMessagesOpen = useCallback(() => messagesOpenRef.current, [])

  useImperativeHandle(ref, () => ({
    openMessages,
    isMessagesOpen,
    getLocalParticipantId: lifecycle.getLocalParticipantId,
    leave: () => {
      if (continuityPanelRef.current) continuityPanelRef.current.requestClose(() => { void leave() })
      else void leave()
    },
  }), [isMessagesOpen, leave, lifecycle.getLocalParticipantId, openMessages])

  // Turning a device on while the browser reports it blocked, missing, or in use gives the same
  // guidance the prejoin screen shows; Daily still retries so a freshly granted permission works.
  const lastLocalToggleRef = useRef(0)
  const toggleMic = useCallback(() => {
    if (!micOn && isNativeNoiseReductionPending(daily)) {
      showDeviceNotice("Wait for noise reduction to finish, then unmute your microphone.")
      return
    }
    const message = micOn ? null : describeDeviceState(micState, "microphone")
    showDeviceNotice(message || null)
    lastLocalToggleRef.current = Date.now()
    daily?.setLocalAudio(!micOn)
  }, [daily, micOn, micState, showDeviceNotice])
  const toggleCam = useCallback(() => {
    if (!daily || switchingTo) return
    if (camOn) cancelVideoEffectOperations(daily)
    else if (videoEffects.pending) return
    else if (videoEffects.error && videoEffects.desired && videoEffects.desired.type !== "none") {
      lastLocalToggleRef.current = Date.now()
      void retryCameraWithVideoEffect(daily)
      return
    }
    const message = camOn ? null : describeDeviceState(camState, "camera")
    showDeviceNotice(message || null)
    lastLocalToggleRef.current = Date.now()
    daily?.setLocalVideo(!camOn)
  }, [camOn, camState, daily, showDeviceNotice, switchingTo, videoEffects.desired, videoEffects.error, videoEffects.pending])
  const inCallRef = useRef(inCall)
  inCallRef.current = inCall
  useDailyEvent("camera-error", useCallback((event: any) => {
    if (!inCallRef.current) return
    showDeviceNotice(describeCameraError(event))
  }, [showDeviceNotice]))
  // Only attribute a local media shutdown to a host when Daily reports a remote request;
  // device and processor failures can also turn media off without a local button press.
  const localMediaRef = useRef({ audio: micOn, video: camOn, remoteVideoOff: Boolean(daily?.participants()?.local?.tracks?.video?.off?.byRemoteRequest) })
  // Moving between breakout rooms leaves and re-joins, which drops local media for a moment;
  // that is not a host action.
  const switchingRoomsRef = useRef(false)
  switchingRoomsRef.current = Boolean(switchingTo)
  useDailyEvent("participant-updated", useCallback((event: any) => {
    const participant = event?.participant
    if (!participant?.local) return
    const previous = localMediaRef.current
    const next = { audio: Boolean(participant.audio), video: Boolean(participant.video), remoteVideoOff: Boolean(participant.tracks?.video?.off?.byRemoteRequest) }
    if (next.remoteVideoOff && !previous.remoteVideoOff && daily) cancelVideoEffectOperations(daily)
    localMediaRef.current = next
    if (!inCallRef.current || switchingRoomsRef.current || Date.now() - lastLocalToggleRef.current < HOST_MEDIA_CHANGE_GRACE_MS) return
    if (previous.audio && !next.audio && participant.tracks?.audio?.off?.byRemoteRequest === true) {
      showDeviceNotice("A host muted your microphone. Unmute when you are ready to speak.")
    } else if (previous.video && !next.video && participant.tracks?.video?.off?.byRemoteRequest === true) {
      showDeviceNotice("A host turned your camera off. Turn it back on when you are ready.")
    }
  }, [daily, showDeviceNotice]))
  const toggleScreenShare = useCallback(() => {
    if (isSharingScreen) {
      stopScreenShare()
    } else {
      // Ask the browser for tab/system audio too; Chrome then offers "Also share tab audio"
      // in its picker, and browsers without audio capture simply return video only.
      startScreenShare({ displayMediaOptions: { audio: true, video: true, systemAudio: "include" } })
    }
  }, [isSharingScreen, startScreenShare, stopScreenShare])
  const toggleFullscreen = useCallback(() => {
    const element = containerRef.current
    if (!element) return
    if (document.fullscreenElement) {
      void document.exitFullscreen?.()
      return
    }
    void element.requestFullscreen?.()
  }, [])
  const handleJoin = useCallback((choice: PrejoinChoice) => {
    void join(choice)
  }, [join])
  const handleLeave = useCallback(() => {
    if (continuityPanelRef.current) continuityPanelRef.current.requestClose(() => { void leave() })
    else void leave()
  }, [leave])
  const { enter: enterPip, exit: exitPip } = pip
  const enterPictureInPicture = useCallback(() => {
    void enterPip().then((result) => {
      if (!result.ok) showDeviceNotice(describePictureInPictureFailure(result.reason))
    })
  }, [enterPip, showDeviceNotice])
  const returnFromPictureInPicture = useCallback(() => {
    window.focus()
    void exitPip()
  }, [exitPip])
  // Screen sharing only arms the browser's tab-switch action. Opening here as well
  // created a second call view while the meeting tab was still visible.

  const meetingTitle = activeMeeting?.title || "WiseFriend meeting"
  const hostName = meeting?.host?.name || (activeMeeting && "host" in activeMeeting ? (activeMeeting as any).host?.name : null)
  const questionsUrl = questions.showPanel && joinInfo.questions_panel?.enabled ? joinInfo.questions_panel.url : undefined
  const messagesPanelToken = useMemo(() => getPanelTokenFromUrl(joinInfo.messages_panel?.url), [joinInfo.messages_panel?.url])
  // Keep only draft text above the disposable Tools workspace. Never persist it
  // across calls/participants, and do not keep hidden activity panels polling.
  const messageDraftScope = JSON.stringify([meetingId, messagesPanelToken, attendee.userId, attendee.isGuest])
  const { capture: captureMessageOwner } = useToolOwnership(messageDraftScope)
  const [messageDraftSession, setMessageDraftSession] = useState<{ scope: string; drafts: MessageDrafts; sendingThreads: Record<string, boolean> }>(() => ({ scope: messageDraftScope, drafts: {}, sendingThreads: {} }))
  if (messageDraftSession.scope !== messageDraftScope) {
    setMessageDraftSession({ scope: messageDraftScope, drafts: {}, sendingThreads: {} })
  }
  const setMessageDrafts = useCallback<MessageDraftState["setDrafts"]>((update) => {
    setMessageDraftSession((previous) => {
      if (previous.scope !== messageDraftScope) return previous
      return { ...previous, drafts: typeof update === "function" ? update(previous.drafts) : update }
    })
  }, [messageDraftScope])
  const setMessageSendingThreads = useCallback<MessageDraftState["setSendingThreads"]>((update) => {
    setMessageDraftSession((previous) => {
      if (previous.scope !== messageDraftScope) return previous
      return { ...previous, sendingThreads: typeof update === "function" ? update(previous.sendingThreads) : update }
    })
  }, [messageDraftScope])
  const messageDraftState = useMemo<MessageDraftState>(() => ({
    captureOwner: captureMessageOwner,
    drafts: messageDraftSession.scope === messageDraftScope ? messageDraftSession.drafts : {},
    setDrafts: setMessageDrafts,
    sendingThreads: messageDraftSession.scope === messageDraftScope ? messageDraftSession.sendingThreads : {},
    setSendingThreads: setMessageSendingThreads,
  }), [messageDraftScope, messageDraftSession, setMessageDrafts, setMessageSendingThreads, captureMessageOwner])
  const messagesEnabled = Boolean(messages.enabled && joinInfo.messages_panel?.enabled && messagesPanelToken)
  const continuityCohortId = meeting?.cohort_id || undefined
  const continuity = useMentorshipRelationships(continuityCohortId, inCall && attendee.isAuthenticated && !attendee.isGuest)
  const currentRelationships = continuity.relationships.filter((relationship) => relationship.is_current)
  const canUseContinuity = Boolean(continuityCohortId && currentRelationships.length)
  // A pair's own 1:1 opens the tools on For next time once the record is available, so the topics
  // are in view from the start. Phone-sized layouts keep the panel closed (it would cover the video),
  // and closing it afterwards is respected.
  const autoOpenedContinuityRef = useRef(false)
  useEffect(() => {
    if (autoOpenedContinuityRef.current || !inCall || !canUseContinuity || !joinInfo.continuity?.pair_meeting || panelDrawer) return
    autoOpenedContinuityRef.current = true
    setActiveTab("nexttime")
    setPanelOpen(true)
  }, [canUseContinuity, inCall, joinInfo.continuity?.pair_meeting, panelDrawer])
  const invitedIds = meeting?.invited_user_ids || []
  const initialContinuity = currentRelationships.find((relationship) => invitedIds.some((id) => String(id) === String(relationship.mentee.id))) || currentRelationships[0]

  // The panel's home is the meeting details with the tools listed underneath (no tab strip, so nothing
  // wraps at narrow widths). Chat and direct messages are reached from the controls bar and People.
  const tools = useMemo<WorkspaceTool[]>(() => {
    const items: WorkspaceTool[] = [{ id: "meeting", label: "Meeting" }]
    items.push({ id: "chat", label: "In-call chat", description: "Messages to everyone in the call right now", badge: chat.unreadCount || undefined })
    items.push({ id: "people", label: "People", description: messagesEnabled ? "Who is here, raised hands, and direct messages" : "Who is here and raised hands", badge: messagesEnabled ? messages.unreadCount : undefined })
    if (questionsUrl) items.push({ id: "questions", label: "Questions", description: "Ask and review questions for this meeting" })
    if (breakout.isHost || breakout.session) items.push({ id: "breakout", label: "Breakout rooms", description: breakout.session ? "A breakout session is running" : "Split the meeting into small groups", live: Boolean(breakout.session) })
    if (canUseContinuity) items.push({ id: "nexttime", label: "For next time", description: "Your relationship notes and discussion topics" })
    if (inspirePanel) items.push({ id: "inspire", label: "Inspire Cards", description: inspireActive ? "A card is live" : "Deal a card or share a response", live: inspireActive })
    if (pollPanel) items.push({ id: "polls", label: "Polls", description: pollLive ? "A poll is live" : "Ask a question and vote together", live: pollLive })
    return items
  }, [breakout.isHost, breakout.session, canUseContinuity, chat.unreadCount, inspireActive, inspirePanel, messages.unreadCount, messagesEnabled, questionsUrl, pollPanel, pollLive])
  const overlays = useMemo<Partial<Record<WorkspaceToolId, WorkspaceOverlay>>>(() => ({
    chat: { label: "In-call chat", onBack: () => closeOverlay() },
    ...(messagesEnabled ? { messages: { label: "Direct messages", backLabel: "Back to People", onBack: () => closeOverlay("people") } } : {}),
  }), [closeOverlay, messagesEnabled])

  useEffect(() => {
    if (!tools.some((tool) => tool.id === activeTab) && !overlays[activeTab]) {
      setActiveTab("meeting")
    }
  }, [activeTab, overlays, tools])

  // The stage is taken only while there is something to look at (a card being collected or a shared
  // response); after "Put the card away" the videos come back while the Inspire tab stays live.
  const inspirePlacement = useMemo<InspireStagePlacement>(() => ({ active: inspireContentLive, onStage: inspireOnStage, setOnStage: setInspireOnStage }), [inspireContentLive, inspireOnStage])
  const inspireStage = inspirePanel && inspireContentLive && inspireOnStage
    ? <InspireStage meetingId={meetingId} panel={inspirePanel} onControlAction={sendControlAction} refreshKey={inspireSyncVersion} onHide={() => setInspireOnStage(false)} />
    : undefined
  const pollStage = pollLive && polls.onStage ? <MeetingPollStage controller={polls} /> : undefined

  const panels = useMemo(() => ({
    meeting: <MeetingDetailsPanel meeting={activeMeeting} hostName={hostName} />,
    chat: <ChatPanel messages={chat.messages} onSend={chat.sendMessage} />,
    questions: questionsUrl ? <PanelIframe title="Questions" src={questionsUrl} testId="call-questions-panel" /> : null,
    messages: messagesEnabled ? <MeetingMessagesPanel embed={{ meetingId, panelToken: messagesPanelToken, visible: messagesVisible, draftState: messageDraftState, openTarget: messagesTarget, onMessageSent: sendDirectMessageSignal }} /> : null,
    people: (
      <PeoplePanel
        mobileMeetingId={joinInfo.native_calls_enabled ? meetingId : undefined}
        waitRoom={waitRoom}
        waitingRoomEnabled={Boolean(joinInfo.waiting_room_enabled)}
        pinnedId={pinnedId}
        onTogglePin={togglePin}
        messaging={messagesEnabled ? {
          unreadCount: messages.unreadCount,
          onOpen: () => openMessages(),
          onMessage: (recipientIdentityKey) => openMessages({ recipientIdentityKey }),
        } : undefined}
      />
    ),
    breakout: <BreakoutPanel breakout={breakout} meetingId={meetingId} />,
    nexttime: canUseContinuity ? <ForNextTimePanel ref={continuityPanelRef} cohortId={continuityCohortId || ""} initialRelationshipId={initialContinuity?.id} currentUserId={attendee.userId} variant="dark" /> : null,
    inspire: inspirePanel ? <InspireInline meetingId={meetingId} panel={inspirePanel} onControlAction={sendControlAction} refreshKey={inspireSyncVersion} placement={inspirePlacement} /> : null,
    polls: pollPanel ? <MeetingPollTools controller={polls} /> : null,
  }), [activeMeeting, attendee.userId, breakout, canUseContinuity, chat.messages, chat.sendMessage, continuityCohortId, hostName, initialContinuity?.id, inspirePanel, inspirePlacement, inspireSyncVersion, joinInfo.waiting_room_enabled, joinInfo.native_calls_enabled, meetingId, messageDraftState, messages.unreadCount, messagesEnabled, messagesPanelToken, messagesTarget, messagesVisible, openMessages, pinnedId, questionsUrl, sendControlAction, sendDirectMessageSignal, togglePin, waitRoom, pollPanel, polls])

  const showPanel = inCall && panelOpen
  // The panel slides open and closed (docked: its width; drawer: up from the bottom) instead of popping.
  const panelPresence = usePanelPresence(showPanel)
  // Keep a relationship draft alive when Tools closes or moves between phone
  // and desktop layouts; leaving the call still uses the shared draft guard.
  const [hasOpenedTools, setHasOpenedTools] = useState(false)
  useEffect(() => { if (panelPresence.mounted) setHasOpenedTools(true) }, [panelPresence.mounted])
  const panelWidth = layout.mode === "desktop" ? 380 : layout.mode === "tablet" ? 320 : 280

  return (
    <CallPreferencesProvider value={callPreferences}>
    <RaisedHandsProvider value={hands}>
    <div
      ref={containerRef}
      data-testid="custom-call-surface"
      data-call-stage={stage}
      data-call-layout={layout.mode}
      data-inspire-active={inspireActive ? "true" : undefined}
      data-inspire-on-stage={inspireStage ? "true" : undefined}
      className="flex h-full w-full flex-col bg-[#0f1419] text-white"
    >
      <WorkspaceHeader
        title={meetingTitle}
        startTime={activeMeeting?.start_time}
        endTime={activeMeeting?.end_time}
        participantCount={participantCount}
        onLeave={handleLeave}
        panelOpen={panelOpen}
        onTogglePanel={togglePanel}
        unreadCount={messages.unreadCount + chat.unreadCount}
        badges={inCall ? <RecordingTranscriptionBadges compact={layout.isCompact} /> : null}
        compact={layout.isCompact}
        mobile={layout.isMobile}
        inCall={inCall}
        networkThreshold={networkThreshold}
        networkOpen={networkOpen}
        onToggleNetwork={() => setNetworkOpen((value) => !value)}
        roomLabel={breakout.location.kind === "room" ? `${breakout.location.roomName}${breakoutCountdown ? ` · ends in ${breakoutCountdown}` : ""}` : breakoutCountdown ? `Breakout ends in ${breakoutCountdown}` : null}
      />
      {(chatNotice.message || messages.notification) && (
          <section aria-label="Message notifications" data-testid="call-message-notifications" className="mx-3 my-1 flex w-[min(22rem,calc(100%-1.5rem))] shrink-0 flex-col gap-2 self-end overflow-y-auto p-0.5" style={{ maxHeight: '20%' }}>
            {messages.notification && <MessageNotification kind="direct" senderName={messages.notification.toast.senderName} body={messages.notification.toast.body} onOpen={messages.notification.onOpen} onDismiss={messages.notification.onDismiss} />}
            <ChatNotification message={chatNotice.message} onDismiss={chatNotice.dismiss} onOpen={() => {
              chatNotice.dismiss()
              setActiveTab("chat")
              setPanelOpen(true)
            }} />
          </section>
      )}
      <div className="relative flex min-h-0 flex-1">
        <main className="relative min-h-0 min-w-0 flex-1">
          {(stage === "prejoin" || stage === "joining") && (
            <PrejoinScreen
              meetingTitle={meetingTitle}
              displayName={attendee.displayName}
              joining={stage === "joining"}
              micOn={micOn}
              camOn={camOn}
              onToggleMic={toggleMic}
              onToggleCam={toggleCam}
              onJoin={handleJoin}
              onCancel={handleLeave}
              waitingRoomEnabled={requiresMeetingAdmission(joinInfo)}
            />
          )}
          {stage === "waiting" && (
            <WaitingScreen meetingTitle={meetingTitle} requesting={lifecycle.requestingAccess} onLeave={handleLeave} />
          )}
          {stage === "disconnected" && (
            <div data-testid="call-disconnected" className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
              <WifiOff className="h-8 w-8 text-amber-200" aria-hidden="true" />
              <h1 className="text-xl font-semibold">You were disconnected</h1>
              <p className="max-w-md text-sm text-white/70">{lifecycle.disconnectMessage || "Rejoin with your camera, microphone, and device settings."}</p>
              <button type="button" disabled={Boolean(switchingTo)} onClick={() => void lifecycle.rejoin()} className="rounded-full bg-[#f6a35c] px-6 py-3 font-semibold text-[#1c2530] disabled:opacity-50">
                {switchingTo ? "Rejoining…" : "Rejoin"}
              </button>
            </div>
          )}
          {stage === "left" && (
            <div className="flex h-full w-full items-center justify-center text-white/70">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Leaving...
            </div>
          )}
          {(inCall || stage === "disconnected") && (
            <div hidden={!inCall} className="h-full w-full" style={{ paddingBottom: CONTROLS_BAR_RESERVED_SPACE }}>
              <Stage viewMode={viewMode} layoutMode={layout.mode} stageVideoRef={stageVideoRef} pinnedId={pinnedId} onTogglePin={togglePin} featured={pollStage || (pollLive ? undefined : inspireStage)} onStopScreenShare={isSharingScreen ? toggleScreenShare : undefined} />
            </div>
          )}
          {inCall && (
            <>
              {switchingTo && (
                <div role="status" data-testid="call-room-switching" className="absolute inset-0 z-[60] flex items-center justify-center gap-2 bg-[#0f1419]/85 text-sm font-semibold text-white">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Moving you to {switchingTo}...
                </div>
              )}
              {breakout.pendingInvite && !breakout.isHost && !switchingTo && (
                <div role="status" data-testid="call-breakout-invite" className="absolute left-1/2 top-3 z-[46] flex -translate-x-1/2 items-center gap-3 rounded-xl bg-[#1c2530] px-3 py-2 text-xs font-medium text-white shadow-2xl ring-1 ring-white/10">
                  <span>You&apos;ve been assigned to {breakout.pendingInvite.room_name}.</span>
                  <button type="button" disabled={breakout.busy} onClick={() => void breakout.joinPendingInvite()} className="rounded-full bg-[#f6a35c] px-3 py-1 text-xs font-semibold text-[#1c2530] hover:bg-[#f7b071] disabled:opacity-50">
                    Join {breakout.pendingInvite.room_name}
                  </button>
                </div>
              )}
              {lifecycle.reconnecting && (
                <div role="status" data-testid="call-reconnecting" className="absolute left-1/2 top-3 z-[45] flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/70 px-3 py-1.5 text-xs font-semibold text-amber-100">
                  <WifiOff className="h-3.5 w-3.5" />
                  Reconnecting...
                </div>
              )}
              {(deviceNotice || videoEffects.error) && (
                <div
                  role="status"
                  data-testid="call-device-notice"
                  className={`absolute left-1/2 z-[45] flex max-w-[min(90vw,32rem)] -translate-x-1/2 items-start gap-2 rounded-xl bg-black/80 px-3 py-2 text-xs font-medium text-amber-100 ring-1 ring-white/10 ${lifecycle.reconnecting ? "top-12" : "top-3"}`}
                >
                  <div className="min-w-0">
                    <span>{videoEffects.error || deviceNotice}</span>
                    {videoEffects.error && videoEffects.desired && videoEffects.desired.type !== "none" && <button type="button" disabled={Boolean(videoEffects.pending || switchingTo)} onClick={() => { lastLocalToggleRef.current = Date.now(); void retryCameraWithVideoEffect(callObject) }} className="mt-2 block min-h-9 rounded-lg bg-white/10 px-3 py-2 text-left font-semibold text-white hover:bg-white/20 disabled:opacity-50">{videoEffects.pending === "retry" ? "Retrying camera…" : "Retry camera with background"}</button>}
                  </div>
                  {!videoEffects.error && <button type="button" aria-label="Dismiss device notice" onClick={() => showDeviceNotice(null)} className="-my-0.5 rounded px-1 text-base leading-none text-white/70 hover:text-white">
                    ×
                  </button>}
                </div>
              )}
              <ReactionsOverlay reactions={reactions} announcement={reactionAnnouncement} />

              {reactionsOpen && <ReactionsMenu onSelect={sendReaction} onClose={() => setReactionsOpen(false)} skinTone={preferences.emojiSkinTone} />}
              {moreOpen && (
                <MoreMenu
                  viewMode={viewMode}
                  onChangeViewMode={setViewMode}
                  isFullscreen={isFullscreen}
                  fullscreenAvailable={!layout.isCompact}
                  onToggleFullscreen={toggleFullscreen}
                  pipAvailable={pip.supported}
                  onEnterPictureInPicture={enterPictureInPicture}
                  onOpenSettings={openSettings}
                  onOpenReactions={layout.isMobile ? () => setReactionsOpen(true) : undefined}
                  networkThreshold={networkThreshold}
                  onOpenNetwork={layout.isMobile ? () => setNetworkOpen(true) : undefined}
                  onClose={() => setMoreOpen(false)}
                />
              )}
              {micMenuOpen && <QuickDeviceMenu kind="microphone" onOpenSettings={openSettings} onClose={() => setMicMenuOpen(false)} />}
              {camMenuOpen && <QuickDeviceMenu kind="camera" onOpenSettings={openSettings} onClose={() => setCamMenuOpen(false)} />}
              {networkOpen && <NetworkPanel onClose={() => setNetworkOpen(false)} />}
              {devicesOpen && <CallSettingsDialog initialPane={settingsPane} onMuteMicrophone={() => { lastLocalToggleRef.current = Date.now(); daily?.setLocalAudio(false) }} onClose={() => setDevicesOpen(false)} />}
              <ControlsBar
                micOn={micOn}
                camOn={camOn}
                canSendAudio={canSendAudio}
                canSendVideo={canSendVideo}
                onToggleMic={toggleMic}
                onToggleCam={toggleCam}
                canShareScreen={canShareScreenInThisBrowser()}
                isSharingScreen={isSharingScreen}
                onToggleScreenShare={toggleScreenShare}
                handRaised={hands.localRaised}
                onToggleHand={hands.toggleHand}
                micMenuOpen={micMenuOpen}
                onToggleMicMenu={() => {
                  const next = !micMenuOpen
                  closePopovers()
                  setDevicesOpen(false)
                  setMicMenuOpen(next)
                }}
                micIndicator={<MicLevelMeter active={micOn} />}
                camMenuOpen={camMenuOpen}
                onToggleCamMenu={() => {
                  const next = !camMenuOpen
                  closePopovers()
                  setDevicesOpen(false)
                  setCamMenuOpen(next)
                }}
                reactionsOpen={reactionsOpen}
                onToggleReactions={() => {
                  const next = !reactionsOpen
                  closePopovers()
                  setDevicesOpen(false)
                  setReactionsOpen(next)
                }}
                chatOpen={chatOpen}
                chatUnreadCount={chat.unreadCount}
                onToggleChat={() => {
                  closePopovers()
                  toggleChat()
                }}
                toolsOpen={panelOpen}
                toolsBadge={messages.unreadCount}
                onToggleTools={() => {
                  closePopovers()
                  togglePanel()
                }}
                moreOpen={moreOpen}
                onToggleMore={() => {
                  const next = !moreOpen
                  closePopovers()
                  setDevicesOpen(false)
                  setMoreOpen(next)
                }}
                onLeave={handleLeave}
                compact={layout.isCompact}
                mobile={layout.isMobile}
              />
            </>
          )}
        </main>
        {(panelPresence.mounted || (canUseContinuity && hasOpenedTools)) && (
            <div
              hidden={!panelPresence.mounted}
              className={panelDrawer ? "absolute inset-0 z-[50] transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none" : "shrink-0 overflow-hidden transition-[width] duration-200 ease-out motion-reduce:transition-none"}
              style={panelDrawer ? (panelPresence.state === "open" ? { transform: "translateY(0)", opacity: 1 } : { transform: "translateY(100%)", opacity: 0 }) : { width: panelPresence.state === "open" ? panelWidth : 0 }}
              data-testid={panelDrawer ? "call-panel-drawer" : "call-panel-sidebar"}
              data-state={panelPresence.state}
            >
              <div className={`h-full ${panelDrawer ? "" : "border-l border-white/10"}`} style={panelDrawer ? undefined : { width: panelWidth }}>
                <WorkspacePanels tools={tools} activeTool={activeTab} onToolChange={setActiveTab} onClose={() => setPanelOpen(false)} compact={panelDrawer} panels={panels} overlays={overlays} />
              </div>
            </div>
        )}
      </div>
      {(inCall || stage === "waiting") && <DailyAudio />}
      {inCall && pip.pipWindow && createPortal(
        <PictureInPictureWindow
          micOn={micOn}
          camOn={camOn}
          canSendAudio={canSendAudio}
          canSendVideo={canSendVideo}
          canShareScreen={canShareScreenInThisBrowser()}
          isSharingScreen={isSharingScreen}
          onToggleMic={toggleMic}
          onToggleCam={toggleCam}
          onToggleScreenShare={toggleScreenShare}
          onReturn={returnFromPictureInPicture}
          onLeave={handleLeave}
        />,
        pip.pipWindow.document.body,
      )}
    </div>
    </RaisedHandsProvider>
    </CallPreferencesProvider>
  )
})

function StartingCall() {
  return (
    <div data-testid="custom-call-surface" data-call-stage="creating" className="flex h-full w-full items-center justify-center bg-[#0f1419] text-white/70">
      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
      Starting the call...
    </div>
  )
}

const CallObjectHost = forwardRef<CallSurfaceHandle, CallSurfaceProps>(function CallObjectHost(props, ref) {
  const callObject = useCallObject({ options: CALL_OBJECT_OPTIONS })

  if (!callObject) {
    return <StartingCall />
  }

  return (
    <DailyProvider callObject={callObject}>
      <CustomCallController ref={ref} {...props} callObject={callObject} />
    </DailyProvider>
  )
})

export const CustomDailyCallSurface = forwardRef<CallSurfaceHandle, CallSurfaceProps>(function CustomDailyCallSurface(props, ref) {
  // Clear any instance left by a previous surface before daily-react can reuse or collide with it.
  const [ready, setReady] = useState(false)
  useEffect(() => {
    let active = true
    void destroyStaleCallInstance().finally(() => {
      if (active) setReady(true)
    })
    return () => {
      active = false
    }
  }, [])

  if (!ready) {
    return <StartingCall />
  }

  return <CallObjectHost ref={ref} {...props} />
})
